import React, { useEffect, useLayoutEffect } from 'react';

import PropTypes from 'prop-types';

import {
  identify,
  setBaseProperties,
  waitForBaseProperties,
} from '@adsk/acs-web-analytics';
import {
  AlloyProvider,
  useUser,
  useAlloyFeatureFlag,
} from '@adsk/alloy-provider';
import TopNavigation from '@adsk/alloy-top-navigation';

import MaintenancePageConditionalRenderer from './MaintenancePageConditionalRenderer';
import { initialize as initializePendo } from './pendo';
import TopNavigationLayout from './TopNavigationLayout';
import { useProductsAccessTypes } from './useProductsAccessTypes';

const ConnectedTopNavigation = () => {
  const { user } = useUser();
  const { value: projectDirectorySubscriptionsModalFF } = useAlloyFeatureFlag(
    'project-directory-subscriptions-modal'
  );

  useEffect(() => {
    if (user) {
      initializePendo({ user });
    }
  }, [user]);

  return (
    <TopNavigation
      logo
      user={user}
      showHelpMenu
      showTrialBanner
      showSubscriptionsModal={!!projectDirectorySubscriptionsModalFF}
      helpLinks={{
        help: 'https://help.autodesk.com/view/BUILD/ENU/?guid=Project_Administration',
      }}
      isProductRequired={false}
    />
  );
};

const AnalyticsInitializer = () => {
  const { user } = useUser();
  const { value: cleanLicenses, loading: cleanLicensesLoading } =
    useAlloyFeatureFlag('clean-licenses-layout', false);

  const notApplicable = 'N/A';

  useLayoutEffect(() => {
    waitForBaseProperties('license');
  }, []);

  const { data: productsAccessTypesData, loading: productsAccessTypesLoading } =
    useProductsAccessTypes({ cleanLicenses, cleanLicensesLoading });

  useEffect(() => {
    !productsAccessTypesLoading &&
      user &&
      identify(
        user.analyticsId,
        cleanLicenses ? { ...user, licenses: productsAccessTypesData } : user
      );
  }, [
    cleanLicenses,
    productsAccessTypesData,
    productsAccessTypesLoading,
    user,
  ]);

  useEffect(() => {
    setBaseProperties({
      license: notApplicable,
      tool_id: notApplicable,
      product_id: notApplicable,
      account_id: notApplicable,
      project_id: notApplicable,
    });
  }, []);

  return null;
};

/**
 * ProjectDirectoryLayout component is the root component for project list page views.
 * This component needs to be as up as we can in React components tree, and handles 3 things mainly:
 *
 * 1. Page layout - rendering top navigation component, including projects picker, product picker and more, all according to user's entitlements.
 * 2. Data providers - adds AlloyProvider, which fetches relevant data and allows all children to access the data using hooks (useUser, useProjectEntitlements, etc.)
 * 3. Redirects - redirect the user to pages they can see:
 *       * User is not logged in - redirects to login page.
 *       * When entitlements response contains `valid_service_location` the user needs to go to.
 */
const ProjectDirectoryLayout = React.forwardRef(({ children, locale }, ref) => (
  <AlloyProvider locale={locale} autoFetchEntities={[]} disableAllProjectsFetch>
    <AnalyticsInitializer />
    <MaintenancePageConditionalRenderer>
      <TopNavigationLayout
        ref={ref}
        renderTopNavigation={(props) => <ConnectedTopNavigation {...props} />}
      >
        {children}
      </TopNavigationLayout>
    </MaintenancePageConditionalRenderer>
  </AlloyProvider>
));

ProjectDirectoryLayout.displayName = 'ProjectDirectoryLayout';

ProjectDirectoryLayout.propTypes = {
  /** Page content */
  children: PropTypes.node,
  /** localization, default: en */
  locale: PropTypes.string,
};

export default ProjectDirectoryLayout;
